import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Phone, PhoneOff, X, Check, MessageCircle, Clock, Loader2 } from 'lucide-react';
import { apiClient } from '../lib/api.js';

type Outcome =
  | 'contact_made'
  | 'will_pay'
  | 'no_answer'
  | 'voicemail'
  | 'refused'
  | 'wrong_number'
  | 'callback_later';

const OUTCOMES: Array<{ id: Outcome; label: string; emoji: string; color: string }> = [
  { id: 'will_pay', label: 'Vai pagar agora', emoji: '💰', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25' },
  { id: 'contact_made', label: 'Falei mas vou seguir', emoji: '💬', color: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40 hover:bg-cyan-500/25' },
  { id: 'callback_later', label: 'Pediu pra ligar depois', emoji: '⏰', color: 'bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25' },
  { id: 'no_answer', label: 'Não atendeu', emoji: '📵', color: 'bg-slate-500/15 text-slate-300 border-slate-500/40 hover:bg-slate-500/25' },
  { id: 'voicemail', label: 'Caiu na caixa', emoji: '📨', color: 'bg-slate-500/15 text-slate-300 border-slate-500/40 hover:bg-slate-500/25' },
  { id: 'refused', label: 'Disse que não', emoji: '🚫', color: 'bg-rose-500/15 text-rose-300 border-rose-500/40 hover:bg-rose-500/25' },
  { id: 'wrong_number', label: 'Número errado', emoji: '❌', color: 'bg-slate-500/15 text-slate-300 border-slate-500/40 hover:bg-slate-500/25' },
];

export function CallDialog({
  open,
  callId,
  contactName,
  phone,
  onClose,
}: {
  open: boolean;
  callId: string | null;
  contactName: string;
  phone: string | null;
  onClose: (result?: { outcome: Outcome }) => void;
}): JSX.Element | null {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [notes, setNotes] = useState('');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) return;
    setOutcome(null);
    setNotes('');
    setElapsed(0);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  const complete = useMutation({
    mutationFn: () => {
      if (!callId || !outcome) throw new Error('missing_state');
      return apiClient.callComplete(callId, { outcome, notes: notes.trim() || undefined });
    },
    onSuccess: () => onClose({ outcome: outcome! }),
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-cyan-300">
              <Phone className="h-5 w-5" />
              <h2 className="text-lg font-bold">Ligando…</h2>
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              {contactName} {phone && <span className="text-foreground">· {phone}</span>}
            </div>
          </div>
          <button onClick={() => onClose()} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 flex items-center gap-2 text-xs">
          <Clock className="h-4 w-4 text-cyan-300" />
          <span className="font-mono tabular-nums">
            {Math.floor(elapsed / 60).toString().padStart(2, '0')}:{(elapsed % 60).toString().padStart(2, '0')}
          </span>
          <span className="text-muted-foreground">tempo da ligação</span>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
            Como foi a ligação?
          </div>
          <div className="space-y-1.5">
            {OUTCOMES.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setOutcome(o.id)}
                className={`w-full text-left px-3 py-2 rounded-md border text-sm flex items-center gap-2 transition-colors ${o.color} ${outcome === o.id ? 'ring-2 ring-offset-2 ring-offset-card ring-current' : ''}`}
              >
                <span className="text-base">{o.emoji}</span>
                <span className="flex-1">{o.label}</span>
                {outcome === o.id && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Nota (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Ex: cliente disse que paga em 2h via PIX"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => onClose()}
            disabled={complete.isPending}
            className="px-3 py-2 rounded-md text-xs border border-border text-muted-foreground hover:text-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => complete.mutate()}
            disabled={!outcome || complete.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 text-emerald-950 text-xs font-bold hover:bg-emerald-400 disabled:opacity-60"
          >
            {complete.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Salvar ligação
          </button>
        </div>
      </div>
    </div>
  );
}
